import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SettingsService from '../services/settingsService';
import { getQuickDateRange } from '../utils/dateUtils';

const INITIAL_FILTERS = {
  location: '',
  locationCoords: null,
  startDate: '',
  endDate: '',
  priceRange: 'any',
  category: 'all',
  subCategory: null,
  selectedCategories: [],
  selectedCategoryItems: [],
  specialAttributes: [],
  whoGoing: [],
  maxDistance: 15,
  transportMode: 'any',
  startingPointType: 'city-center',
  startingPointAddress: '',
  startingPointCoords: null,
  searchMode: 'categories',
  textQuery: ''
};

/**
 * Custom hook managing the Home page search form state and handlers.
 * Handles: filter state, quick dates, who's going display, category selection,
 * special attributes, starting points, text search mode, and search navigation.
 */
function useHomeSearch() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchFilters, setSearchFilters] = useState(INITIAL_FILTERS);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showWhosGoingModal, setShowWhosGoingModal] = useState(false);
  const [homeLocation, setHomeLocation] = useState('');

  // Load home location on mount
  useEffect(() => {
    const loadUserData = async () => {
      const home = await SettingsService.getHomeLocation();
      setHomeLocation(home);
    };
    loadUserData();
  }, []);

  // Listen for settings updates
  useEffect(() => {
    const handleSettingsUpdate = async () => {
      const home = await SettingsService.getHomeLocation();
      setHomeLocation(home);
    };
    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('settingsUpdated', handleSettingsUpdate);
  }, []);

  const handleFilterChange = useCallback((key, value) => {
    setSearchFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleExpandableCategoryChange = useCallback((selectedItems) => {
    const selectedCategories = selectedItems.map(item => item.category);
    const category = selectedCategories.length === 0 ? 'all' :
                     selectedCategories.length === 1 ? selectedCategories[0] :
                     'multi';

    setSearchFilters(prev => ({
      ...prev,
      selectedCategoryItems: selectedItems,
      selectedCategories: [...new Set(selectedCategories)],
      category,
      subCategory: null
    }));
  }, []);

  const handleSearch = useCallback(() => {
    const params = new URLSearchParams();

    if (searchFilters.location) params.set('location', searchFilters.location);
    if (searchFilters.locationCoords) {
      params.set('lat', searchFilters.locationCoords.lat.toString());
      params.set('lng', searchFilters.locationCoords.lng.toString());
    }
    if (searchFilters.startDate) params.set('startDate', searchFilters.startDate);
    if (searchFilters.endDate) params.set('endDate', searchFilters.endDate);
    if (searchFilters.priceRange && searchFilters.priceRange !== 'any') params.set('priceRange', searchFilters.priceRange);
    if (searchFilters.category && searchFilters.category !== 'all') params.set('category', searchFilters.category);
    if (searchFilters.subCategory) params.set('subCategory', searchFilters.subCategory);
    if (searchFilters.maxDistance) params.set('maxDistance', searchFilters.maxDistance.toString());

    // Store complex data in sessionStorage
    if (searchFilters.whoGoing && searchFilters.whoGoing.length > 0) {
      sessionStorage.setItem('searchFilters_whoGoing', JSON.stringify(searchFilters.whoGoing));
      params.set('whoGoingCount', searchFilters.whoGoing.length.toString());
    } else {
      sessionStorage.removeItem('searchFilters_whoGoing');
    }
    if (searchFilters.groupWeights) {
      sessionStorage.setItem('searchFilters_groupWeights', JSON.stringify(searchFilters.groupWeights));
    } else {
      sessionStorage.removeItem('searchFilters_groupWeights');
    }

    if (searchFilters.selectedCategories && searchFilters.selectedCategories.length > 0) {
      sessionStorage.setItem('searchFilters_selectedCategories', JSON.stringify(searchFilters.selectedCategories));
    } else {
      sessionStorage.removeItem('searchFilters_selectedCategories');
    }

    if (searchFilters.selectedCategoryItems && searchFilters.selectedCategoryItems.length > 0) {
      sessionStorage.setItem('searchFilters_selectedCategoryItems', JSON.stringify(searchFilters.selectedCategoryItems));
    } else {
      sessionStorage.removeItem('searchFilters_selectedCategoryItems');
    }

    if (searchFilters.specialAttributes && searchFilters.specialAttributes.length > 0) {
      sessionStorage.setItem('searchFilters_specialAttributes', JSON.stringify(searchFilters.specialAttributes));
    } else {
      sessionStorage.removeItem('searchFilters_specialAttributes');
    }

    // Starting point parameters
    if (searchFilters.startingPointType && searchFilters.startingPointType !== 'city-center') {
      params.set('startingPointType', searchFilters.startingPointType);

      // No address for current-location — the results side derives its display
      // label from startingPointType, so no language-specific text enters the
      // (shareable) URL for that type.
      let startingAddress = '';
      if (searchFilters.startingPointType === 'home' && homeLocation) {
        startingAddress = homeLocation;
      } else if (searchFilters.startingPointType === 'custom' && searchFilters.startingPointAddress) {
        startingAddress = searchFilters.startingPointAddress;
      }

      if (startingAddress) {
        params.set('startingPointAddress', startingAddress);
      }

      if (searchFilters.startingPointCoords) {
        params.set('startingPointLat', searchFilters.startingPointCoords.lat.toString());
        params.set('startingPointLng', searchFilters.startingPointCoords.lng.toString());
      }
    }

    // Text search mode
    if (searchFilters.searchMode === 'text' && searchFilters.textQuery) {
      params.set('searchMode', 'text');
      params.set('textQuery', searchFilters.textQuery);
    }

    navigate(`/search-results?${params.toString()}`);
  }, [searchFilters, homeLocation, navigate]);

  const setQuickDate = useCallback((type) => {
    const dateType = type === 'week' ? '7days' : type;
    const { startDate, endDate } = getQuickDateRange(dateType);
    if (startDate && endDate) {
      setSearchFilters(prev => ({ ...prev, startDate, endDate }));
    }
  }, []);

  const isQuickDateSelected = useCallback((type) => {
    const { startDate, endDate } = searchFilters;
    if (!startDate) return false;
    const dateType = type === 'week' ? '7days' : type;
    const expected = getQuickDateRange(dateType);
    return startDate === expected.startDate && endDate === expected.endDate;
  }, [searchFilters]);

  const handleWhosGoingSave = useCallback((selectedPeople, weights) => {
    setSearchFilters(prev => ({ ...prev, whoGoing: selectedPeople, groupWeights: weights || null }));
  }, []);

  const getWhosGoingDisplay = useCallback(() => {
    const { whoGoing } = searchFilters;

    if (whoGoing.length === 0) return 'Just you';

    if (whoGoing.length === 1) {
      const isCurrentUser = currentUser && whoGoing[0].id === currentUser.id;
      return isCurrentUser ? 'Just you' : whoGoing[0].name;
    }

    if (whoGoing.length === 2) {
      const hasCurrentUser = currentUser && whoGoing.some(p => p.id === currentUser.id);
      if (hasCurrentUser) {
        const otherPerson = whoGoing.find(p => p.id !== currentUser.id);
        return otherPerson ? `You & ${otherPerson.name}` : 'Just you';
      }
      return `${whoGoing[0].name} & ${whoGoing[1].name}`;
    }

    const hasCurrentUser = currentUser && whoGoing.some(p => p.id === currentUser.id);
    if (hasCurrentUser) return `You & ${whoGoing.length - 1} others`;
    return `${whoGoing[0].name} & ${whoGoing.length - 1} others`;
  }, [searchFilters, currentUser]);

  return {
    searchFilters,
    setSearchFilters,
    showAdvancedFilters,
    setShowAdvancedFilters,
    showWhosGoingModal,
    setShowWhosGoingModal,
    homeLocation,
    handleFilterChange,
    handleExpandableCategoryChange,
    handleSearch,
    setQuickDate,
    isQuickDateSelected,
    handleWhosGoingSave,
    getWhosGoingDisplay
  };
}

export default useHomeSearch;
